/**
 * Result sections for ValidationResultsDisplay.
 *
 * Extracted from ImportModal.tsx (Sub-PR 6.4).
 */

import { useTranslation } from 'react-i18next'
import { AlertCircle, AlertTriangle, CheckCircle2, FileText } from 'lucide-react'
import type { ImportValidationResult } from '../../../types/infrastructure'

const ERROR_PREVIEW_COUNT = 5
const DUP_PREVIEW_COUNT = 5
const IP_CONFLICT_PREVIEW_COUNT = 3

interface SectionProps {
  result: ImportValidationResult
}

export function ResultSummary({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-start gap-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="flex-shrink-0">
        {result.valid ? (
          <CheckCircle2 className="h-6 w-6 text-green-500" />
        ) : (
          <AlertCircle className="h-6 w-6 text-red-500" />
        )}
      </div>
      <div>
        <h3 className="font-medium text-slate-900 dark:text-white">
          {result.valid
            ? t('infrastructure.import.fileValid')
            : t('infrastructure.import.validationFailed')}
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {t('infrastructure.import.recordsSummary', {
            fileName: result.fileName,
            total: result.totalRecords,
            valid: result.validRecords,
          })}
        </p>
        {result.batchId && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t('infrastructure.import.batchId', { id: result.batchId })}
          </p>
        )}
      </div>
    </div>
  )
}

export function ErrorsSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
      <div className="mb-2 flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <h4 className="font-medium text-red-800 dark:text-red-300">
          {t('infrastructure.import.validationErrors', { count: result.errors.length })}
        </h4>
      </div>
      <ul className="space-y-1 text-sm text-red-700 dark:text-red-400">
        {result.errors.slice(0, ERROR_PREVIEW_COUNT).map((err, i) => (
          <li key={i}>
            {t('infrastructure.import.errorRow', {
              row: err.row,
              field: err.field,
              message: err.message,
            })}
            {err.value && <span className="ml-1 opacity-75">("{err.value}")</span>}
          </li>
        ))}
        {result.errors.length > ERROR_PREVIEW_COUNT && (
          <li className="opacity-75">
            {t('infrastructure.import.andMoreErrors', {
              count: result.errors.length - ERROR_PREVIEW_COUNT,
            })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function DuplicatesInFileSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const dups = result.duplicates.hostnameInFile
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
      <div className="mb-2 flex items-center gap-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <h4 className="font-medium text-red-800 dark:text-red-300">
          {t('infrastructure.import.duplicateHostnamesInFile', { count: dups.length })}
        </h4>
      </div>
      <p className="mb-2 text-sm text-red-700 dark:text-red-400">
        {t('infrastructure.import.duplicateHostnamesInFileDesc')}
      </p>
      <ul className="space-y-1 text-sm text-red-700 dark:text-red-400">
        {dups.slice(0, DUP_PREVIEW_COUNT).map((dup, i) => (
          <li key={i}>
            {dup.hostname} ({t('infrastructure.import.rows')}: {dup.rows.join(', ')})
          </li>
        ))}
        {dups.length > DUP_PREVIEW_COUNT && (
          <li className="opacity-75">
            {t('infrastructure.import.andMore', { count: dups.length - DUP_PREVIEW_COUNT })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function DuplicatesInDbSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const dups = result.duplicates.hostnameInDatabase
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <h4 className="font-medium text-amber-800 dark:text-amber-300">
          {t('infrastructure.import.existingHostnames', { count: dups.length })}
        </h4>
      </div>
      <p className="mb-2 text-sm text-amber-700 dark:text-amber-400">
        {t('infrastructure.import.existingHostnamesDesc')}
      </p>
      <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-400">
        {dups.slice(0, DUP_PREVIEW_COUNT).map((dup, i) => (
          <li key={i}>{dup.hostname}</li>
        ))}
        {dups.length > DUP_PREVIEW_COUNT && (
          <li className="opacity-75">
            {t('infrastructure.import.andMore', { count: dups.length - DUP_PREVIEW_COUNT })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function IpConflictsSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const conflicts = result.duplicates.ipAddressConflicts
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500" />
        <h4 className="font-medium text-amber-800 dark:text-amber-300">
          {t('infrastructure.import.ipConflicts', { count: conflicts.length })}
        </h4>
      </div>
      <p className="mb-2 text-sm text-amber-700 dark:text-amber-400">
        {t('infrastructure.import.ipConflictsDesc')}
      </p>
      <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-400">
        {conflicts.slice(0, IP_CONFLICT_PREVIEW_COUNT).map((conflict, i) => (
          <li key={i}>
            {conflict.ipAddress} ({conflict.hostnames.join(', ')})
          </li>
        ))}
        {conflicts.length > IP_CONFLICT_PREVIEW_COUNT && (
          <li className="opacity-75">
            {t('infrastructure.import.andMore', {
              count: conflicts.length - IP_CONFLICT_PREVIEW_COUNT,
            })}
          </li>
        )}
      </ul>
    </div>
  )
}

export function WarningsSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <div className="mb-2 flex items-center gap-2">
        <FileText className="h-5 w-5 text-slate-500" />
        <h4 className="font-medium text-slate-700 dark:text-slate-300">
          {t('infrastructure.import.warnings', { count: result.warnings.length })}
        </h4>
      </div>
      <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
        {result.warnings.map((warn, i) => (
          <li key={i}>{warn.message}</li>
        ))}
      </ul>
    </div>
  )
}

export function PreviewSection({ result }: SectionProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <h4 className="mb-2 font-medium text-slate-700 dark:text-slate-300">
        {t('infrastructure.import.preview', { count: result.preview.length })}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="py-2 pr-4 text-left">{t('infrastructure.import.colRow')}</th>
              <th className="py-2 pr-4 text-left">{t('infrastructure.import.colHostname')}</th>
              <th className="py-2 pr-4 text-left">{t('infrastructure.import.colName')}</th>
              <th className="py-2 pr-4 text-left">{t('infrastructure.import.colType')}</th>
              <th className="py-2 text-left">{t('infrastructure.import.colIPs')}</th>
            </tr>
          </thead>
          <tbody>
            {result.preview.map((row) => (
              <tr key={row.row} className="border-b border-slate-100 dark:border-slate-800">
                <td className="py-2 pr-4 text-slate-500 dark:text-slate-400">{row.row}</td>
                <td className="py-2 pr-4 font-mono text-xs">{row.hostname}</td>
                <td className="py-2 pr-4">{row.name}</td>
                <td className="py-2 pr-4">{row.deviceType}</td>
                <td className="py-2 text-xs">{row.ipAddresses.join(', ') || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
